// Shared visible-text matching keeps find, highlighting and replacement in sync.
export const SEARCH_FORMATS = [
	['plain', 'No formatting'], ['bold', 'Bold'], ['italic', 'Italic'],
	['underline', 'Underline'], ['superscript', 'Superscript'], ['subscript', 'Subscript'],
];
export const defaultSearchFormats = Object.fromEntries(SEARCH_FORMATS.map(([key]) => [key, true]));
// An empty selection means any formatting.
export const formatsFromSelection = (selected = []) => selected.length
	? Object.fromEntries(SEARCH_FORMATS.map(([key]) => [key, selected.includes(key)]))
	: defaultSearchFormats;

const formatsOn = (element, root) => {
	const formats = new Set();
	for (let el = element; el && el !== root; el = el.parentElement) {
		const tag = el.tagName.toLowerCase();
		const style = el.style;
		if (['b', 'strong', 'sastrong'].includes(tag) || /^(bold|[6-9]00)$/.test(style.fontWeight)) formats.add('bold');
		if (['i', 'em', 'saitalic'].includes(tag) || style.fontStyle === 'italic') formats.add('italic');
		if (['u', 'saunderline'].includes(tag) || /underline/.test(style.textDecoration)) formats.add('underline');
		if (['sup', 'sasup'].includes(tag) || style.verticalAlign === 'super') formats.add('superscript');
		if (['sub', 'sasub'].includes(tag) || style.verticalAlign === 'sub') formats.add('subscript');
	}
	return formats;
};

export const searchRoot = (html) => {
	const root = document.createElement('div');
	root.innerHTML = html || '';
	return root;
};

// \b only knows ASCII letters, so accented and non-Latin words need explicit boundaries.
// Built with RegExp strings because the Next.js Babel setup cannot compile \p{...} literals.
const buildSearchRegex = (pattern, wholeWords, caseSensitive) => {
	const flags = 'gm' + (caseSensitive ? '' : 'i');
	if (!wholeWords) return new RegExp(pattern, flags);
	try {
		const word = '[\\p{L}\\p{M}\\p{N}_]';
		return new RegExp(`(?<!${word})(?:${pattern})(?!${word})`, flags + 'u');
	} catch (error) {
		// Unicode mode rejects some legacy regex syntax; fall back to ASCII word boundaries.
		return new RegExp(`\\b(?:${pattern})\\b`, flags);
	}
};

export const searchMatches = (root, options = {}, target = true) => {
	const { search = '', useRegex, caseSensitive, wholeWords, targetFormats = defaultSearchFormats } = options;
	if (!search) return [];
	let pattern = useRegex ? search : search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const regex = buildSearchRegex(pattern, wholeWords, caseSensitive);
	const runs = [];
	let text = '';
	const visit = node => {
		if (node.nodeType === 3) {
			const start = text.length;
			text += node.textContent;
			runs.push({ node, start, end: text.length, formats: formatsOn(node.parentElement, root) });
		} else if (node.nodeType === 1) {
			if (node !== root && node.matches('img, br, script, style, .xlftag, [contenteditable="false"]')) {
				// A barrier prevents phrases from spanning an inline placeholder.
				runs.push({ barrier: true, start: text.length, end: text.length });
				return;
			}
			node.childNodes.forEach(visit);
		}
	};
	visit(root);
	const matches = [];
	for (const match of text.matchAll(regex)) {
		if (!match[0].length) continue;
		const start = match.index;
		const end = start + match[0].length;
		if (runs.some(run => run.barrier && run.start > start && run.start < end)) continue;
		const covered = runs.filter(run => !run.barrier && run.end > start && run.start < end);
		if (target && !covered.every(run => run.formats.size
			? [...run.formats].some(format => targetFormats[format])
			: targetFormats.plain)) continue;
		matches.push({ start, length: end - start, match, runs: covered });
	}
	return matches;
};

// The segment renderer parses XHTML. DOM innerHTML drops the closing slash
// from placeholders, which would make the entire segment fail XML parsing,
// and writes non-breaking spaces as &nbsp;, which XML does not define.
export const serializeSearchRoot = (root) => root.innerHTML.replace(/&nbsp;/g, '\u00a0').replace(/<(img|br|hr|wbr)\b((?:[^"'<>]|"[^"]*"|'[^']*')*)>/gi,
	(_match, tag, attributes) => `<${tag}${attributes.replace(/\/\s*$/, '')} />`);

export const replaceSearchMatches = (html, options, all = true) => {
	const root = searchRoot(html);
	const matches = searchMatches(root, options);
	const selected = all ? matches : matches.slice(0, 1);
	if (!selected.length) return html;
	// Work backwards so offsets in the original text nodes remain valid.
	selected.reverse().forEach(({ start, length, match, runs }) => {
		const first = runs[0];
		const last = runs[runs.length - 1];
		const range = document.createRange();
		range.setStart(first.node, start - first.start);
		range.setEnd(last.node, start + length - last.start);
		let replacement = options.replace ?? '';
		if (options.useRegex) {
			// Apply JS replacement tokens using the original match and captures.
			replacement = replacement.replace(/\$\$|\$&|\$`|\$'|\$<([^>]+)>|\$(\d{1,2})/g, (token, name, number) => {
				if (token === '$$') return '$';
				if (token === '$&') return match[0];
				if (token === '$`') return match.input.slice(0, match.index);
				if (token === "$'") return match.input.slice(match.index + match[0].length);
				if (name) return match.groups ? (match.groups[name] ?? '') : token;
				if (Number(number) > 0 && Number(number) < match.length) return match[Number(number)] ?? '';
				if (number.length === 2 && Number(number[0]) > 0 && Number(number[0]) < match.length) return (match[Number(number[0])] ?? '') + number[1];
				return token;
			});
		}
		// Deleting across wrappers can move a Range to their common parent.
		// A live collapsed range at the first text position preserves its style.
		const insertionPoint = document.createRange();
		insertionPoint.setStart(first.node, start - first.start);
		insertionPoint.collapse(true);
		range.deleteContents();
		const inserted = document.createElement('span');
		inserted.textContent = replacement;
		insertionPoint.insertNode(inserted);
		if (options.replacementFormat && options.replacementFormat !== 'preserve') {
			// Split surrounding inline wrappers to isolate replacement formatting.
			while (inserted.parentElement !== root && !/^(P|DIV|LI)$/.test(inserted.parentElement.tagName)) {
				const parent = inserted.parentElement;
				const after = parent.cloneNode(false);
				while (inserted.nextSibling) after.appendChild(inserted.nextSibling);
				parent.after(inserted, after);
				if (!parent.hasChildNodes()) parent.remove();
				if (!after.hasChildNodes()) after.remove();
			}
			const tags = { bold: 'sastrong', italic: 'saitalic', underline: 'saunderline', superscript: 'sasup', subscript: 'sasub' };
			const tag = tags[options.replacementFormat];
			if (tag) {
				const wrapper = document.createElement(tag);
				wrapper.append(...inserted.childNodes);
				inserted.appendChild(wrapper);
			}
		}
		inserted.replaceWith(...inserted.childNodes);
	});
	// Replacing text can leave empty format wrappers behind.
	root.querySelectorAll('sastrong, saitalic, saunderline, sasup, sasub, b, i, u, strong, em, sup, sub, span')
		.forEach(element => { if (!element.textContent && !element.children.length && !element.matches('.xlftag, [contenteditable]')) element.remove(); });
	return serializeSearchRoot(root);
};

export const markSearchMatches = (instance, root, options, target = true) => {
	instance.unmark({ className: 'search-highlight' });
	try {
		const ranges = searchMatches(root, options, target).map(({ start, length }) => ({ start, length }));
		instance.markRanges(ranges, { element: 'highlight', className: 'search-highlight' });
	} catch (error) {
		// Invalid expressions are reported by the Find & Replace panel.
	}
};
